// AriaBusinessOnboarding.jsx
// 5-question onboarding modal for blue-collar business owners.
// Captures: trade · crew size (=stage) · mindset · energy · communication style.
// POSTs to /api/aria/onboarding/save → backend writes owner_profile.
//
// Usage:
//   <AriaBusinessOnboarding
//     onComplete={(profile) => { ... }}
//     onSkip={() => { ... }}
//   />
//
// Visual language matches aria-dashboard-demo.html: dark theme, cyan accents,
// Liquid Glass aesthetic. Mobile-responsive (single column on narrow viewports).

import React, { useState, useCallback, useEffect } from 'react';
import { getApiBase, getAriaUserIdForApi } from './apiBase';

const QUESTIONS = [
  {
    id: 'trade',
    q: 'What trade are you in?',
    options: [
      { v: 'electrician',         l: '⚡ Electrician' },
      { v: 'plumber',             l: '🔧 Plumber' },
      { v: 'hvac',                l: '❄️ HVAC' },
      { v: 'roofer',              l: '🏠 Roofer' },
      { v: 'landscaper',          l: '🌿 Landscaper' },
      { v: 'painter',             l: '🎨 Painter' },
      { v: 'handyman',            l: '🔨 Handyman' },
      { v: 'general_contractor',  l: '🏗 General contractor' },
      { v: 'cleaning',            l: '🧽 Cleaning' },
      { v: 'pest_control',        l: '🐜 Pest control' },
      { v: 'other',               l: '🛠 Something else' },
    ],
  },
  {
    id: 'stage',
    q: 'How big is your crew right now?',
    options: [
      { v: 'solo',         l: 'Just me' },
      { v: 'first_hires',  l: '1–3 helpers' },
      { v: 'crew_leader',  l: '4–10 employees' },
      { v: 'multi_crew',   l: '11–25 employees' },
      { v: 'mature',       l: '25+ employees' },
    ],
  },
  {
    id: 'mindset',
    q: "What's your focus right now?",
    options: [
      { v: 'survival',     l: 'Just keeping it going — cash is tight' },
      { v: 'growth',       l: 'Ready to grow — need more leads + crews' },
      { v: 'optimization', l: 'Steady — want to squeeze better margins' },
      { v: 'stability',    l: 'Steady — don\'t break what\'s working' },
      { v: 'legacy',       l: 'Building something to outlast me' },
      { v: 'exit',         l: 'Preparing to sell or hand off' },
    ],
  },
  {
    id: 'energy',
    q: 'How are you feeling about it lately?',
    options: [
      { v: 'fired_up',     l: 'Fired up — let\'s go' },
      { v: 'in_the_grind', l: 'In the grind — heads down' },
      { v: 'plateaued',    l: 'A bit stuck — same numbers all year' },
      { v: 'burning_out',  l: 'Honestly? Burning out' },
      { v: 'recovering',   l: 'Coming back from a rough stretch' },
    ],
  },
  {
    id: 'comm_style',
    q: 'How should I talk to you?',
    options: [
      { v: 'direct',  l: 'Direct — just give me the answer' },
      { v: 'coach',   l: 'Coach — walk me through it' },
      { v: 'friend',  l: 'Friend — keep it warm' },
      { v: 'numbers', l: 'Numbers — show me the data' },
      { v: 'visual',  l: 'Visual — show me, don\'t tell me' },
    ],
  },
];

const STAGE_LABEL = {
  solo: 'solo grinder',
  first_hires: 'first-hires owner',
  crew_leader: 'crew leader',
  multi_crew: 'multi-crew operator',
  mature: 'mature operator',
};

const COMM_RECAP = {
  direct: 'straight — no fluff',
  coach: 'with options + tradeoffs',
  friend: 'like a person, not a robot',
  numbers: 'with the numbers up front',
  visual: 'with charts + visuals where possible',
};

const ENERGY_RECAP = {
  fired_up:     "I'll match your speed.",
  in_the_grind: 'Steady cadence — morning brief, midday check, evening recap.',
  plateaued:    "I'll surface the next-jump opportunities when you're ready.",
  burning_out:  "I'll cap interruptions to 2 a day — you have enough on your plate.",
  recovering:   "I'll go gentle. One brief a day until you tell me otherwise.",
};

export default function AriaBusinessOnboarding({ onComplete, onSkip, allowSkip = true }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const isFinalRecap = stepIdx === QUESTIONS.length;
  const step = isFinalRecap ? null : QUESTIONS[stepIdx];

  const handlePick = useCallback((value) => {
    if (!step) return;
    setAnswers((prev) => ({ ...prev, [step.id]: value }));
    setStepIdx((i) => i + 1);
  }, [step]);

  const handleBack = useCallback(() => {
    if (stepIdx > 0) setStepIdx((i) => i - 1);
  }, [stepIdx]);

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const apiBase = getApiBase() || '';
      const userId = getAriaUserIdForApi() || '';
      const url = `${apiBase}/api/aria/onboarding/save`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-Id': userId } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(answers),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Save failed (${res.status}): ${txt || 'unknown error'}`);
      }
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Save failed');
      setDone(true);
      setTimeout(() => {
        if (typeof onComplete === 'function') onComplete(data.profile || answers);
      }, 600); // brief celebratory hold
    } catch (err) {
      setError(err.message || String(err));
      setSubmitting(false);
    }
  }, [answers, onComplete]);

  // Submit on entering recap
  useEffect(() => {
    if (isFinalRecap && !submitting && !done && !error) {
      submit();
    }
    // Only re-evaluate when recap entry condition changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinalRecap]);

  const progress = Math.min(((stepIdx) / QUESTIONS.length) * 100, 100);

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Aria onboarding">
      <div style={styles.modal}>
        {/* Progress bar */}
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressBar, width: `${progress}%` }} />
        </div>

        {!isFinalRecap && step && (
          <>
            <div style={styles.eyebrow}>
              Aria · question {stepIdx + 1} of {QUESTIONS.length}
            </div>
            <h2 style={styles.heading}>{step.q}</h2>

            <div style={styles.optList}>
              {step.options.map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => handlePick(opt.v)}
                  style={styles.optBtn}
                  onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.optBtnHover)}
                  onMouseLeave={(e) => Object.assign(e.currentTarget.style, styles.optBtn)}
                >
                  {opt.l}
                </button>
              ))}
            </div>

            <div style={styles.footRow}>
              {stepIdx > 0 ? (
                <button type="button" onClick={handleBack} style={styles.linkBtn}>← Back</button>
              ) : <span />}
              {allowSkip && (
                <button type="button" onClick={onSkip} style={styles.linkBtn}>Skip for now</button>
              )}
            </div>
          </>
        )}

        {isFinalRecap && (
          <>
            <div style={styles.eyebrow}>{done ? 'Aria · ready' : submitting ? 'Aria · saving your profile' : 'Aria · error'}</div>

            {!error && (
              <h2 style={styles.heading}>
                Got it. You're a <span style={styles.accent}>{STAGE_LABEL[answers.stage] || answers.stage}</span> in{' '}
                <span style={styles.accent}>{answers.mindset}</span> mode.
              </h2>
            )}

            {!error && (
              <p style={styles.bodyText}>
                I'll talk to you {COMM_RECAP[answers.comm_style] || 'directly'}. {ENERGY_RECAP[answers.energy] || ''}
              </p>
            )}

            {error && (
              <>
                <h2 style={{ ...styles.heading, color: '#ff453a' }}>Couldn't save your profile</h2>
                <p style={styles.bodyText}>{error}</p>
                <button
                  type="button"
                  onClick={submit}
                  style={styles.cta}
                  disabled={submitting}
                >
                  {submitting ? 'Retrying…' : 'Try again'}
                </button>
              </>
            )}

            {submitting && !done && !error && (
              <div style={styles.spinnerRow}>
                <span style={styles.spinner} />
                <span style={{ color: '#9ca3af', fontSize: 13 }}>Saving…</span>
              </div>
            )}

            {done && (
              <button type="button" onClick={() => onComplete && onComplete(answers)} style={styles.cta}>
                Show me my dashboard ›
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.78)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
    animation: 'fadeIn 0.3s ease',
  },
  modal: {
    background: 'linear-gradient(155deg, rgba(20,20,28,0.96), rgba(10,10,14,0.96))',
    border: '0.5px solid rgba(0,217,255,0.4)',
    borderRadius: 24,
    padding: '40px 36px',
    maxWidth: 560, width: '100%',
    boxShadow: '0 30px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: '#fff',
    maxHeight: '92vh',
    overflowY: 'auto',
  },
  progressTrack: {
    height: 3, borderRadius: 3,
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 22,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #00d9ff, #5e5ce6)',
    transition: 'width 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)',
  },
  eyebrow: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: '#00d9ff',
    marginBottom: 12,
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '-0.015em',
    lineHeight: 1.3,
    margin: '0 0 22px',
    color: '#fff',
  },
  accent: { color: '#00d9ff', fontWeight: 700 },
  bodyText: {
    fontSize: 14,
    color: '#cbd0d6',
    lineHeight: 1.55,
    margin: '0 0 22px',
  },
  optList: {
    display: 'flex', flexDirection: 'column', gap: 8,
    marginBottom: 18,
  },
  optBtn: {
    background: 'rgba(255,255,255,0.03)',
    border: '0.5px solid rgba(255,255,255,0.1)',
    color: '#fff',
    font: 'inherit',
    fontSize: 14, fontWeight: 500,
    padding: '14px 18px',
    borderRadius: 12,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s',
  },
  optBtnHover: {
    background: 'rgba(0,217,255,0.08)',
    border: '0.5px solid rgba(0,217,255,0.4)',
  },
  footRow: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: 11, color: '#6b7280',
  },
  linkBtn: {
    background: 'transparent', border: 'none',
    color: '#6b7280',
    fontSize: 11.5, fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
  cta: {
    background: 'linear-gradient(135deg, rgba(0,217,255,0.3), rgba(0,217,255,0.18))',
    border: '0.5px solid rgba(0,217,255,0.6)',
    color: '#fff',
    font: 'inherit',
    fontSize: 14, fontWeight: 600,
    padding: '12px 22px',
    borderRadius: 12,
    cursor: 'pointer',
    width: '100%',
    marginTop: 8,
  },
  spinnerRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    marginTop: 8,
  },
  spinner: {
    width: 14, height: 14,
    borderRadius: '50%',
    border: '2px solid rgba(0,217,255,0.25)',
    borderTopColor: '#00d9ff',
    display: 'inline-block',
    animation: 'aria-spin 0.8s linear infinite',
  },
};

// Inject keyframes once (module-level, runs at first import in browser)
if (typeof document !== 'undefined' && !document.getElementById('aria-onboarding-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'aria-onboarding-styles';
  styleEl.textContent = `
    @keyframes aria-spin { to { transform: rotate(360deg); } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  `;
  document.head.appendChild(styleEl);
}

// Helper export so callers can check status before rendering.
// Returns { completed: boolean, profile: object|null } or null on error.
export async function fetchOnboardingStatus() {
  try {
    const apiBase = getApiBase() || '';
    const userId = getAriaUserIdForApi() || '';
    const res = await fetch(`${apiBase}/api/aria/onboarding/status`, {
      method: 'GET',
      headers: userId ? { 'X-User-Id': userId } : {},
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.ok ? { completed: !!data.completed, profile: data.profile } : null;
  } catch {
    return null;
  }
}
